import { useState, useEffect } from 'react';
import { useAuth } from '@/context/SafeAuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';

interface DashboardAnalytics {
  totalEntries: number;
  unstuckRate: string;
  thisWeekEntries: number;
  loading: boolean;
  error: string | null;
}

export const useDashboardAnalytics = (): DashboardAnalytics => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [analytics, setAnalytics] = useState<DashboardAnalytics>({
    totalEntries: 0,
    unstuckRate: '0%',
    thisWeekEntries: 0,
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (!user) {
      setAnalytics(prev => ({ ...prev, loading: false }));
      return;
    }

    fetchAnalytics();
  }, [user]);

  const fetchAnalytics = async () => {
    try {
      setAnalytics(prev => ({ ...prev, loading: true, error: null }));

      // Fetch all journal entries for the current user
      const { data: entries, error } = await supabase
        .from('journal_entries')
        .select('progress, progress_max, created_at')
        .eq('user_id', user!.id);

      if (error) {
        console.error('Error fetching journal entries:', error);
        // Set default values instead of throwing
        setAnalytics({
          totalEntries: 0,
          unstuckRate: '0%',
          thisWeekEntries: 0,
          loading: false,
          error: error.message,
        });
        return;
      }

      const totalEntries = entries?.length || 0;
      
      // Calculate unstuck rate (entries where progress >= progress_max)
      const completedEntries = entries?.filter(entry => 
        entry.progress >= entry.progress_max
      ).length || 0;
      
      const unstuckRate = totalEntries > 0 
        ? Math.round((completedEntries / totalEntries) * 100) 
        : 0;

      // Calculate entries from this week
      const now = new Date();
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - now.getDay()); // Start of current week (Sunday)
      startOfWeek.setHours(0, 0, 0, 0);

      const thisWeekEntries = entries?.filter(entry => {
        const entryDate = new Date(entry.created_at);
        return entryDate >= startOfWeek;
      }).length || 0;

      setAnalytics({
        totalEntries,
        unstuckRate: `${unstuckRate}%`,
        thisWeekEntries,
        loading: false,
        error: null,
      });

    } catch (error: any) {
      console.error('Error fetching dashboard analytics:', error);
      setAnalytics({
        totalEntries: 0,
        unstuckRate: '0%',
        thisWeekEntries: 0,
        loading: false,
        error: error.message || 'Failed to fetch analytics',
      });
    }
  };

  return analytics;
};
